/**
 * MarkeeMix Studio — Unified "My Tasks" dashboard.
 *
 * Shows every task across every connected channel where the current user is
 * an assignee. The single workspace promise — editors, designers, SMMs no
 * longer switch channels.
 */
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowLeft, Clock, AlertTriangle, CheckCircle2, Loader2, Filter, ChevronRight,
  Sparkles, ListChecks, Youtube,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SignInRequiredCard from "../tools/SignInRequiredCard";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

const STAGE_LABEL = {
  idea: "Idea", approved: "Approved", scripting: "Scripting",
  script_review: "Script review", recording: "Recording",
  raw_uploaded: "Raw uploaded", editing: "Editing",
  edit_review: "Edit review", thumbnail: "Thumbnail",
  thumbnail_review: "Thumbnail review", seo: "SEO",
  scheduled: "Scheduled", published: "Published", archived: "Archived",
};

const FILTERS = [
  { key: "all", label: "All open" },
  { key: "today", label: "Today" },
  { key: "overdue", label: "Overdue" },
  { key: "this_week", label: "This week" },
  { key: "high_priority", label: "High priority" },
  { key: "completed", label: "Completed" },
];

const ROLE_LABEL = {
  creator: "Creator", editor: "Editor", designer: "Designer",
  smm: "SMM", reviewer: "Reviewer",
};

export default function StudioMyTasks() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null);
  const [filter, setFilter] = useState("all");
  const [tasks, setTasks] = useState([]);
  const [channels, setChannels] = useState([]);
  const [channelFilter, setChannelFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async (fl) => {
    setLoading(true);
    setError(null);
    try {
      const boot = await axios.get(`${API}/api/studio/bootstrap`, { withCredentials: true });
      setAuthed(!!boot.data?.authenticated);
      if (!boot.data?.authenticated) {
        setLoading(false);
        return;
      }
      const params = fl && fl !== "all" ? { filter: fl } : {};
      const r = await axios.get(`${API}/api/studio/content/my/tasks`, { params, withCredentials: true });
      setTasks(r.data.tasks || []);
      setChannels(r.data.channels || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(filter); }, [filter]);

  const filteredTasks = useMemo(() => {
    let arr = tasks;
    if (channelFilter !== "all") arr = arr.filter((t) => t.channel_id === channelFilter);
    if (filter === "high_priority") arr = arr.filter((t) => t.priority === "high" || (t.sla_days != null && t.sla_days < 0));
    return arr;
  }, [tasks, channelFilter, filter]);

  const counts = useMemo(() => ({
    all: tasks.length,
    today: tasks.filter((t) => t.sla_days === 0).length,
    overdue: tasks.filter((t) => t.sla_days != null && t.sla_days < 0).length,
    this_week: tasks.filter((t) => t.sla_days != null && t.sla_days >= 0 && t.sla_days <= 7).length,
  }), [tasks]);

  return (
    <>
      <Header />
      <div className="studio-shell">
        <button className="studio-back" onClick={() => navigate("/studio")} data-testid="my-tasks-back">
          <ArrowLeft size={14}/> Back to Studio
        </button>

        <motion.div className="studio-hero" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <span className="studio-eyebrow">
            <Sparkles size={12}/> Unified workspace · My tasks
          </span>
          <h1 className="studio-h1">
            Every task you own, <span className="studio-h1-accent">across every channel.</span>
          </h1>
          <p className="studio-sub">
            No switching, no separate logins. Edit / design / publish — all from one place.
          </p>
        </motion.div>

        {!loading && authed === false && (
          <SignInRequiredCard
            icon={<ListChecks size={26} strokeWidth={1.6}/>}
            eyebrow="My Tasks"
            title="Sign in to see your unified task list"
            description="Once you're added to a channel, every video assigned to you appears here automatically."
            benefits={["One inbox across all channels", "Filter by today / overdue / this week", "Click straight into the content card"]}
            testId="my-tasks-signin"
          />
        )}

        {!loading && authed && (
          <>
            <div className="studio-filter-bar" data-testid="my-tasks-filter-bar">
              <div className="studio-filter-chips">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    className="studio-chip"
                    data-active={filter === f.key}
                    onClick={() => setFilter(f.key)}
                    data-testid={`my-tasks-filter-${f.key}`}
                  >
                    {f.label}
                    {counts[f.key] != null && f.key !== "completed" && (
                      <span className="studio-chip-count">{counts[f.key]}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {channels.length > 1 && (
              <div className="studio-channel-pills" data-testid="my-tasks-channel-pills">
                <button
                  className="studio-chip"
                  data-active={channelFilter === "all"}
                  onClick={() => setChannelFilter("all")}
                  data-testid="my-tasks-channel-all"
                >
                  <Youtube size={11}/> All channels
                  <span className="studio-chip-count">{tasks.length}</span>
                </button>
                {channels.map((c) => {
                  const ct = tasks.filter((t) => t.channel_id === c.channel_id).length;
                  return (
                    <button
                      key={c.channel_id}
                      className="studio-chip"
                      data-active={channelFilter === c.channel_id}
                      onClick={() => setChannelFilter(c.channel_id)}
                      data-testid={`my-tasks-channel-${c.channel_id}`}
                    >
                      {c.thumbnail
                        ? <img src={c.thumbnail} alt="" style={{ width: 14, height: 14, borderRadius: 3, objectFit: "cover" }}/>
                        : <Youtube size={11}/>}
                      {c.title}
                      <span className="studio-chip-count">{ct}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {loading && (
              <div className="studio-card studio-loading">
                <Loader2 size={22} className="ac-spin" style={{ color: "var(--st-purple)" }}/>
              </div>
            )}

            {error && <div className="ac-error"><AlertTriangle size={16}/> {error}</div>}

            {!loading && filteredTasks.length === 0 && (
              <div className="studio-empty" data-testid="my-tasks-empty">
                <div className="studio-empty-icon"><ListChecks size={36}/></div>
                <h2>You&apos;re all clear ✨</h2>
                <p>No tasks match this filter. Try another, or wait for new work to land.</p>
              </div>
            )}

            {!loading && filteredTasks.length > 0 && (
              <div className="studio-task-list" data-testid="my-tasks-list">
                {filteredTasks.map((t) => <TaskCard key={t._id} t={t} />)}
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </>
  );
}

function TaskCard({ t }) {
  const slaClass =
    t.sla_days == null ? "" :
    t.sla_days < 0 ? "studio-sla--overdue" :
    t.sla_days === 0 ? "studio-sla--today" :
    t.sla_days <= 2 ? "studio-sla--soon" : "";
  const slaText =
    t.sla_days == null ? "No due date" :
    t.sla_days < 0 ? `Overdue by ${-t.sla_days}d` :
    t.sla_days === 0 ? "Due today" :
    `${t.sla_days}d left`;
  const myRoles = t.my_roles || [];
  const isEditor = myRoles.includes("editor");
  const isDesigner = myRoles.includes("designer");
  const isSmm = myRoles.includes("smm");
  const isCreator = myRoles.includes("creator");

  return (
    <motion.a
      href={`/studio/channels/${t.channel_id}#content-${t._id}`}
      className="studio-task-card"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      data-testid={`task-${t._id}`}
    >
      <div className="studio-task-channel">
        {t.channel_thumbnail ? <img src={t.channel_thumbnail} alt=""/> : <Youtube size={14}/>}
        <small>{t.channel_title}</small>
        {t.priority === "high" && <span className="studio-pill studio-pill--sm studio-pill--bad">High</span>}
      </div>
      <h3>{t.title}</h3>
      <div className="studio-task-meta">
        <span className={`studio-pill studio-pill--sm studio-stage--${t.stage}`}>{STAGE_LABEL[t.stage] || t.stage}</span>
        <span className="studio-pill studio-pill--sm">{myRoles.map((r) => ROLE_LABEL[r] || r).join(" + ") || "—"}</span>
        {t.publish_date && (
          <span className="studio-pill studio-pill--sm">📅 {new Date(t.publish_date).toLocaleDateString()}</span>
        )}
      </div>

      {/* Role-specific quick links */}
      <div className="studio-task-rolebar" onClick={(e) => e.stopPropagation()}>
        {isEditor && (
          <>
            {t.drive?.raw_url && <a href={t.drive.raw_url} target="_blank" rel="noreferrer" className="studio-link">🎬 Raw</a>}
            {t.script && <span className="studio-link" title="Script saved">📝 Script ✓</span>}
            {t.ppt_url && <a href={t.ppt_url.startsWith("http") ? t.ppt_url : `${process.env.REACT_APP_BACKEND_URL || ''}${t.ppt_url}`} target="_blank" rel="noreferrer" className="studio-link">📊 PPT</a>}
          </>
        )}
        {isDesigner && (
          <>
            {t.thumbnail_brief && <span className="studio-link" title="Brief ready">🎨 Brief ✓</span>}
            {(t.thumbnail_urls || []).length > 0 && <span className="studio-link">🖼 {t.thumbnail_urls.length} thumb(s)</span>}
          </>
        )}
        {isSmm && (
          <>
            {t.seo && <span className="studio-link" title="SEO ready">🔍 SEO ✓</span>}
            {t.drive?.edited_url && <a href={t.drive.edited_url} target="_blank" rel="noreferrer" className="studio-link">🎞 Edited</a>}
            {t.youtube_video_id && <span className="studio-link">▶ {t.youtube_video_id.slice(0, 8)}…</span>}
          </>
        )}
        {isCreator && t.topic?.keywords?.length > 0 && (
          <span className="studio-link" title={t.topic.keywords.join(", ")}>🔑 {t.topic.keywords.length} keywords</span>
        )}
      </div>

      <div className={`studio-task-foot ${slaClass}`}>
        <Clock size={12}/> {slaText}
        {t.assigned_by && <small style={{ marginLeft: "auto", marginRight: 6, opacity: 0.7 }}>by {t.assigned_by}</small>}
        <ChevronRight size={14}/>
      </div>
    </motion.a>
  );
}
